import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import Dropdown from 'components/Dropdown'
import PinnedCustomersCard from 'pages/Dashboard/cards/PinnedCustomersCard'

import { useStoreContext } from 'hooks/StoreContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import { color } from 'styles/colors'

const PinnedCustomersDropdown = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { arePagedCustomersLoaded, customers } = useStoreContext()
  const { enabledFeatures } = useFeaturesContext()
  const buttonRef = useRef<HTMLDivElement>(null)
  const hasNoPinnedCustomers = arePagedCustomersLoaded && enabledFeatures?.includes('hehm') && Array.isArray(customers) && customers.length === 0
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  if (arePagedCustomersLoaded && !hasLoadedOnce) {
    setHasLoadedOnce(true)
  }

  const onCloseDropdownCallback = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
    if (event && buttonRef.current?.contains(event.target as Node)) {
      return
    }
    setIsDropdownOpen(false)
  }, [])

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((prevState) => !prevState)
  }, [])

  return (
    <>
      <div ref={buttonRef}>
        <Button
          decoration="secondary"
          aria-label="pinned customers"
          onPress={onDropdownToggle}
          styles={{
            button: {
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 10,
              borderRadius: 12,
              backgroundColor: color('paneBackground'),
              borderColor: color('border'),
              height: 39
            }
          }}
          showSpinner={!hasLoadedOnce}
        >
          <S.PinIcon src={'/img/pin.svg'} alt="Pinned Customers" />
          <S.ButtonText>Customers</S.ButtonText>
          <S.DropdownCaret isDropdownOpen={isDropdownOpen} />
        </Button>
      </div>

      <S.Dropdown
        isOpen={isDropdownOpen}
        onClose={onCloseDropdownCallback}
        $hasNoPinnedCustomers={hasNoPinnedCustomers}
      >
        <PinnedCustomersCard
          variant="two-column"
          pageSize="12"
          showTitle={false}
          showBottomPagination
        />
      </S.Dropdown>
    </>
  )
}

export default PinnedCustomersDropdown

const S = {
  Dropdown: styled(Dropdown).withConfig({
    shouldForwardProp: (prop) => !['$hasNoPinnedCustomers'].includes(prop)
  })<{ $hasNoPinnedCustomers: boolean }>`
    width: ${({ $hasNoPinnedCustomers }) => $hasNoPinnedCustomers ? '400px' : '725px'};
    height: ${({ $hasNoPinnedCustomers }) => $hasNoPinnedCustomers ? '150px' : 'fit-content'};
    min-height: 150px;
    z-index: 1200;
    max-width: 60%;
    right: 0;
    margin-top: 45px;
    margin-right: ${({ $hasNoPinnedCustomers }) => $hasNoPinnedCustomers ? '290px' : '10px'};
    background: ${color('paneBackground')};
    border: 2px solid ${color('paneBackground')};
    border-radius: 8px;

    @media (max-width: 767px) {
      max-width: 96%;
      margin-right: 10px;
    }
    
    @media (width: 768px) {
      max-width: 96%;
      margin-right: ${({ $hasNoPinnedCustomers }) => $hasNoPinnedCustomers ? '40px' : '10px'};
    }
  `,
  DropdownCaret: styled(PlayArrowRoundedIcon).withConfig({
    shouldForwardProp: (prop) => !['isDropdownOpen'].includes(prop)
  }) <{ isDropdownOpen: boolean }>`
    color: ${color('actionableIcon')};
    transform: ${({ isDropdownOpen }) => !isDropdownOpen ? 'rotate(90deg);' : 'rotate(270deg);'}
  `,
  ButtonText: styled.span`
    font-size: 12pt;
    font-weight: 500;
    color: ${color('primaryText')};
  `,
  PinIcon: styled.img`
     width: 20px;
     height: 20px;
  `
}
